import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { CheckSquare, Clock, Loader2, RefreshCw, MessageSquare, CalendarX, Check, X, AlarmClock, LogOut, History, ChevronDown, ChevronRight, Building2, Layers, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BTNS = [
    { value: 'present', label: 'Present', icon: Check, active: 'bg-[#047857] text-white border-[#047857]', idle: 'text-[#047857] border-[#A7F3D0] hover:bg-[#ECFDF5]' },
    { value: 'late', label: 'Late', icon: AlarmClock, active: 'bg-[#D97706] text-white border-[#D97706]', idle: 'text-[#B45309] border-[#FDE68A] hover:bg-[#FFFBEB]' },
    { value: 'left_early', label: 'Left early', icon: LogOut, active: 'bg-[#6D28D9] text-white border-[#6D28D9]', idle: 'text-[#6D28D9] border-[#DDD6FE] hover:bg-[#F5F3FF]' },
    { value: 'absent', label: 'Absent', icon: X, active: 'bg-[#DC2626] text-white border-[#DC2626]', idle: 'text-[#DC2626] border-[#FECACA] hover:bg-[#FEF2F2]' },
];

const LABELS = { present: 'Present', late: 'Late', left_early: 'Left early', absent: 'Absent' };

const StudentRow = ({ s, idx, onMark, busy }) => (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 border-t ${idx % 2 ? 'bg-[#FAF9F6]' : 'bg-white'}`} data-testid={`att-row-${s.id}`}>
        <div className="flex-1 min-w-0">
            <p className="font-medium text-[#1F2937]">{idx + 1}. {s.first_name} {s.last_name} {s.arabic_name && <span className="text-xs text-[#6B7280] ml-2" dir="rtl">{s.arabic_name}</span>}</p>
            <div className="text-xs text-[#6B7280] flex flex-wrap items-center gap-2">
                <span className="font-mono">{s.reg_code}</span>
                {s.marked_at && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />marked {s.marked_at}{s.recorded_by_name ? ` by ${s.recorded_by_name}` : ''}</span>}
                {s.late_sms_sent && <Badge className="bg-[#0F766E] text-white text-[10px] gap-1"><MessageSquare className="h-3 w-3" />Late SMS sent</Badge>}
                {s.is_adult && <Badge variant="outline" className="text-[10px]">18+ · no guardian SMS</Badge>}
            </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
            {STATUS_BTNS.map(b => (
                <button key={b.value} type="button" disabled={busy} onClick={() => onMark(s.id, b.value)} data-testid={`att-${b.value}-${s.id}`}
                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-colors disabled:opacity-60 ${s.status === b.value ? b.active : `bg-white ${b.idle}`}`}>
                    <b.icon className="h-3.5 w-3.5" />{b.label}
                </button>
            ))}
        </div>
    </div>
);

export const ClassRegister = ({ cls, onChange, date }) => {
    const [busy, setBusy] = useState(null);
    const marked = cls.students.filter(s => s.status).length;

    const toggleSms = async (enabled) => {
        try {
            await axios.post(`${API_URL}/api/attendance/register/toggle-late-sms`, { class_id: cls.class_id, enabled }, { withCredentials: true });
            onChange({ ...cls, late_sms_enabled: enabled });
            toast.success(enabled ? 'Late-arrival SMS enabled' : 'Late-arrival SMS disabled for today');
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    };
    const mark = async (studentId, status, force = false) => {
        setBusy(studentId);
        try {
            const r = await axios.post(`${API_URL}/api/attendance/register/mark`, { class_id: cls.class_id, student_id: studentId, status, force, ...(date ? { date } : {}) }, { withCredentials: true });
            onChange({ ...cls, students: cls.students.map(s => s.id === studentId ? { ...s, status, marked_at: r.data.marked_at, recorded_by_name: r.data.recorded_by_name, late_sms_sent: r.data.late_sms_sent } : s) });
            if (r.data.late_sms?.sent) toast.info(`Late-arrival SMS sent to guardians (${r.data.late_sms.to})`);
            else if (r.data.late_sms && !r.data.late_sms.sent) toast.error('Late-arrival SMS failed to send');
            else if (date) toast.success('Saved — this change is recorded in the edit log');
        } catch (e) {
            const d = e.response?.data?.detail;
            if (e.response?.status === 409 && d?.code === 'conflict') {
                const st = cls.students.find(s => s.id === studentId);
                const ok = window.confirm(`${st?.first_name || 'This student'} was already marked ${LABELS[d.previous_status] || d.previous_status} by ${d.by}${d.at ? ` at ${d.at}` : ''} (first session).\n\nOverride with ${LABELS[status] || status}?`);
                if (ok) { setBusy(null); return mark(studentId, status, true); }
            } else toast.error((typeof d === 'string' && d) || 'Failed to mark attendance');
        }
        finally { setBusy(null); }
    };

    return (
        <Card className="card-surface overflow-hidden" data-testid={`att-class-${cls.class_id}`}>
            <CardHeader className="bg-[#064E3B]/5 pb-3">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">{cls.class_name}{cls.arabic_name && <span className="text-sm text-[#6B7280]" dir="rtl">{cls.arabic_name}</span>}</CardTitle>
                        <div className="text-sm text-[#4B5563] flex flex-wrap items-center gap-2 mt-1">
                            <span>{cls.program_name}</span>
                            <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />{cls.start_time} – {cls.end_time}</Badge>
                            <Badge variant="secondary">{marked}/{cls.students.length} marked</Badge>
                            {cls.is_substitute && <Badge className="bg-[#7C3AED] text-white" data-testid={`att-substitute-${cls.class_id}`}>You are the substitute today</Badge>}
                        </div>
                    </div>
                    {!date && <div className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${cls.late_sms_enabled ? 'border-[#A7F3D0] bg-[#ECFDF5]' : 'border-[#FDE68A] bg-[#FFFBEB]'}`}>
                        <MessageSquare className={`h-4 w-4 ${cls.late_sms_enabled ? 'text-[#047857]' : 'text-[#B45309]'}`} />
                        <div className="leading-tight">
                            <Label htmlFor={`sms-${cls.class_id}`} className="text-sm cursor-pointer">Late-arrival SMS to guardians</Label>
                            <p className="text-[11px] text-[#6B7280]">{cls.late_sms_enabled ? `Sent when marked 10+ min after ${cls.start_time}` : 'Off for today — no SMS will be sent'}</p>
                        </div>
                        <Switch id={`sms-${cls.class_id}`} checked={cls.late_sms_enabled} onCheckedChange={toggleSms} data-testid={`att-sms-toggle-${cls.class_id}`} />
                    </div>}
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {cls.students.length === 0 ? <p className="p-6 text-sm text-[#6B7280] text-center">No active students in this class.</p>
                    : cls.students.map((s, i) => <StudentRow key={s.id} s={s} idx={i} onMark={mark} busy={busy === s.id} />)}
            </CardContent>
        </Card>
    );
};

export const PastDays = () => {
    const [classes, setClasses] = useState([]);
    const [classId, setClassId] = useState('');
    const [date, setDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); });
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);
    useEffect(() => {
        axios.get(`${API_URL}/api/attendance/register/my-classes`, { withCredentials: true }).then(r => { const cs = r.data || []; setClasses(cs); if (cs.length >= 1) setClassId(cs[0].id); }).catch(() => {});
    }, []);
    const [notStudyDay, setNotStudyDay] = useState('');
    const selectedClass = classes.find(c => c.id === classId);
    const load = useCallback(async () => {
        if (!classId || !date) return;
        const cls = classes.find(c => c.id === classId);
        const dow = new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long' });
        if (cls?.study_days?.length && !cls.study_days.includes(dow)) { setNotStudyDay(`${dow} is not a study day for this class (${cls.study_days.join(', ')}).`); setData(null); return; }
        setNotStudyDay('');
        try { const r = await axios.get(`${API_URL}/api/attendance/register/day`, { params: { class_id: classId, date }, withCredentials: true }); setData(r.data); }
        catch (e) { setNotStudyDay(e.response?.data?.detail || 'Failed to load'); setData(null); }
    }, [classId, date, classes]);
    useEffect(() => { load(); }, [load]);
    const mark = async (studentId, status) => {
        setBusy(studentId);
        try {
            const r = await axios.post(`${API_URL}/api/attendance/register/mark`, { class_id: classId, student_id: studentId, status, date }, { withCredentials: true });
            setData(d => ({ ...d, students: d.students.map(s => s.id === studentId ? { ...s, status, marked_at: r.data.marked_at, recorded_by_name: r.data.recorded_by_name } : s) }));
            toast.success('Saved — this change is recorded for the school admin');
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        finally { setBusy(null); }
    };
    const today = new Date().toISOString().slice(0, 10);
    return (
        <div className="space-y-4" data-testid="att-past-days">
            <Card className="card-surface"><CardContent className="p-4 flex flex-wrap gap-3 items-end">
                <div className="space-y-1"><Label className="text-xs">Class</Label>
                    <Select value={classId} onValueChange={setClassId}><SelectTrigger className="w-[260px]" data-testid="att-past-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                        <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.program_name ? `${c.program_name} — ` : ''}{c.english_name || c.name || c.code}{c.arabic_name ? ` · ${c.arabic_name}` : ''}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={date} max={today} onChange={e => setDate(e.target.value)} data-testid="att-past-date" /></div>
                <p className="text-xs text-[#6B7280] pb-2">{selectedClass?.study_days?.length ? `Study days: ${selectedClass.study_days.join(', ')}. ` : ''}Every change here is logged (who, when, old → new) for the super admin.</p>
            </CardContent></Card>
            {notStudyDay && <Card className="border-[#FDE68A] bg-[#FFFBEB]"><CardContent className="p-4 text-sm text-[#92400E]" data-testid="att-past-not-study-day">{notStudyDay}</CardContent></Card>}
            {!classId ? <Card className="card-surface"><CardContent className="p-8 text-center text-sm text-[#6B7280]">Choose a class and a date.</CardContent></Card>
                : data && (
                    <Card className="card-surface overflow-hidden">
                        <CardHeader className="bg-[#064E3B]/5 pb-3"><CardTitle className="text-base font-['Outfit']">{data.class_name} — {new Date(`${data.date}T12:00:00`).toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}{!data.has_records && <Badge variant="outline" className="ml-2">No register taken that day</Badge>}</CardTitle></CardHeader>
                        <CardContent className="p-0">{data.students.map((s, i) => <StudentRow key={s.id} s={s} idx={i} onMark={mark} busy={busy === s.id} />)}</CardContent>
                    </Card>
                )}
        </div>
    );
};

const GroupHeader = ({ level, icon: Icon, name, sub, count, marked, open, onClick, testId }) => {
    const styles = ['bg-[#064E3B] text-white hover:bg-[#065F46] px-4 py-3', 'bg-[#EDE9FE] text-[#5B21B6] hover:bg-[#DDD6FE] px-6 py-2.5', 'bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A] px-8 py-2'][level];
    return (
        <button type="button" onClick={onClick} className={`w-full flex items-center justify-between ${styles}`} data-testid={testId}>
            <span className="flex items-center gap-2 font-medium">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}<Icon className="h-4 w-4" />{name}{sub && <span className="text-xs font-normal opacity-80">{sub}</span>}</span>
            <span className="text-xs">{count} students · {marked} marked</span>
        </button>
    );
};

export const GroupedRegister = ({ classes, onChange, date }) => {
    const [open, setOpen] = useState({});
    const isOpen = (k, dflt) => (open[k] === undefined ? dflt : open[k]);
    const toggle = (k, dflt) => setOpen(o => ({ ...o, [k]: !isOpen(k, dflt) }));
    const tree = {};
    classes.forEach(c => {
        const b = tree[c.branch || 'No branch'] ||= { name: c.branch || 'No branch', programs: {} };
        const p = b.programs[c.program_id] ||= { name: c.program_name, classes: [] };
        p.classes.push(c);
    });
    const cnt = (list) => list.reduce((a, c) => a + c.students.length, 0);
    const mk = (list) => list.reduce((a, c) => a + c.students.filter(s => s.status).length, 0);
    return (
        <div className="rounded-xl overflow-hidden border border-[#E5E7EB] bg-white" data-testid="grouped-register">
            {Object.values(tree).sort((a, b) => a.name.localeCompare(b.name)).map(b => {
                const bk = `b:${b.name}`; const bc = Object.values(b.programs).flatMap(p => p.classes);
                return (
                    <div key={bk}>
                        <GroupHeader level={0} icon={Building2} name={b.name} count={cnt(bc)} marked={mk(bc)} open={isOpen(bk, true)} onClick={() => toggle(bk, true)} testId={`reg-branch-${b.name}`} />
                        {isOpen(bk, true) && Object.values(b.programs).sort((a, c) => a.name.localeCompare(c.name)).map(p => {
                            const pk = `${bk}|${p.name}`;
                            return (
                                <div key={pk}>
                                    <GroupHeader level={1} icon={Layers} name={p.name} count={cnt(p.classes)} marked={mk(p.classes)} open={isOpen(pk, true)} onClick={() => toggle(pk, true)} testId={`reg-program-${p.name}`} />
                                    {isOpen(pk, true) && p.classes.map(c => {
                                        const ck = `${pk}|${c.class_id}`;
                                        return (
                                            <div key={ck}>
                                                <GroupHeader level={2} icon={BookOpen} name={c.class_name} sub={`${c.arabic_name ? c.arabic_name + ' · ' : ''}${c.teacher_name || 'Unassigned'} · ${c.start_time}–${c.end_time}`} count={c.students.length} marked={c.students.filter(s => s.status).length} open={isOpen(ck, false)} onClick={() => toggle(ck, false)} testId={`reg-class-${c.class_id}`} />
                                                {isOpen(ck, false) && <div className="p-3 bg-[#FAF9F6]"><ClassRegister cls={c} onChange={onChange} date={date} /></div>}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};

export const TodayRegister = ({ grouped = false, date, programId, classId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const fetchData = useCallback(async () => {
        setLoading(true);
        try { const r = await axios.get(`${API_URL}/api/attendance/register/today`, { params: date ? { date } : {}, withCredentials: true }); setData(r.data); }
        catch (e) { toast.error(e.response?.data?.detail || 'Failed to load register'); setData({ classes: [] }); }
        finally { setLoading(false); }
    }, [date]);
    useEffect(() => { fetchData(); }, [fetchData]);
    const updateClass = (updated) => setData(d => ({ ...d, classes: d.classes.map(c => c.class_id === updated.class_id ? updated : c) }));
    const dateLabel = data ? new Date(`${data.date}T12:00:00`).toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-[#4B5563]" data-testid="att-date">{dateLabel}{data?.now ? ` · now ${data.now}` : ''}</p>
                <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="att-refresh"><RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
            </div>
            {loading && !data ? <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div>
                : !data || data.classes.length === 0 ? (
                    <Card className="card-surface"><CardContent className="flex flex-col items-center justify-center py-16" data-testid="att-empty">
                        <CalendarX className="h-16 w-16 text-[#4B5563]/30 mb-4" />
                        <p className="text-[#4B5563] text-lg">No classes scheduled {date ? 'on that day' : 'today'}</p>
                        <p className="text-sm text-[#9CA3AF]">The register only opens on study days (holidays excluded).</p>
                    </CardContent></Card>
                ) : (() => {
                    const visible = data.classes.filter(c => (!programId || c.program_id === programId) && (!classId || c.class_id === classId));
                    if (visible.length === 0) return <Card className="card-surface"><CardContent className="p-8 text-center text-sm text-[#6B7280]" data-testid="att-register-no-match">No class matches the selected program / class filter{date ? ' on that day' : ' today'}.</CardContent></Card>;
                    return grouped ? <GroupedRegister classes={visible} onChange={updateClass} date={data.is_today ? undefined : data.date} />
                        : visible.map(c => <ClassRegister key={c.class_id} cls={c} onChange={updateClass} date={data.is_today ? undefined : data.date} />);
                })()}
        </div>
    );
};

export const AdminPastDays = ({ programId, classId }) => {
    const [date, setDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); });
    const today = new Date().toISOString().slice(0, 10);
    return (
        <div className="space-y-4" data-testid="admin-past-days">
            <Card className="card-surface"><CardContent className="p-4 flex flex-wrap gap-3 items-end">
                <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={date} max={today} onChange={e => setDate(e.target.value)} data-testid="att-past-date" /></div>
                <p className="text-xs text-[#6B7280] pb-2">Only classes with a session on that day are shown. Every change is logged (who, when, old → new).</p>
            </CardContent></Card>
            <TodayRegister grouped date={date} programId={programId} classId={classId} />
        </div>
    );
};
